import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import axiosInstance from "../../utils/axiosInstance";

const ServiceProviderForm = ({ existingData, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    service: "",
    phone: "",
    location: "",
    verified: false,
    working_days: "",
    working_hours: "",
    pricing_range: "",
    extra_info: "",
    category_id: "",
    image: null,
  });

  const [categories, setCategories] = useState([]);
  const isEditing = Boolean(existingData);

  // ✅ Fetch categories safely, ensure correct endpoint
  const fetchCategories = async () => {
    try {
      const res = await axiosInstance.get("/services/categories/"); // <-- trailing slash fixes route mixup

      // ✅ Handle multiple API response shapes gracefully
      const cats = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data.data)
        ? res.data.data
        : res.data.categories || [];

      // ✅ Filter out invalid items (just in case backend sends weird data)
      const validCats = cats.filter(
        (c) => c && (c.id || c._id) && (c.name || c.title)
      );

      setCategories(validCats);
    } catch (err) {
      console.error("❌ Failed to fetch categories:", err);
      setCategories([]); // fallback
    }
  };

  useEffect(() => {
    fetchCategories();

    if (existingData) {
      setFormData({
        name: existingData.name || "",
        service: existingData.service || "",
        phone: existingData.phone || "",
        location: existingData.location || "",
        verified: existingData.verified || false,
        working_days: existingData.working_days || "",
        working_hours: existingData.working_hours || "",
        pricing_range: existingData.pricing_range || "",
        extra_info: existingData.extra_info || "",
        category_id:
          existingData.category?.id ||
          existingData.category?._id ||
          existingData.category_id ||
          "",
        image: null,
      });
    }
  }, [existingData]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") {
      setFormData({ ...formData, [name]: checked });
    } else if (type === "file") {
      setFormData({ ...formData, image: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = new FormData();
    Object.keys(formData).forEach((key) => {
      if (formData[key] !== null) form.append(key, formData[key]);
    });

    try {
      if (isEditing) {
        await axiosInstance.put(`/services/${existingData.id}`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        await axiosInstance.post("/services", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      onClose();
    } catch (err) {
      console.error("❌ Failed to save provider:", err);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isEditing ? "Edit Service Provider" : "Add Service Provider"}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            fullWidth
            margin="dense"
            required
          />
          <TextField
            label="Service"
            name="service"
            value={formData.service}
            onChange={handleChange}
            fullWidth
            margin="dense"
            required
          />
          <TextField
            label="Phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            fullWidth
            margin="dense"
            required
          />
          <TextField
            label="Location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            fullWidth
            margin="dense"
            required
          />

          {/* ✅ Robust category dropdown */}
          <TextField
            select
            label="Category"
            name="category_id"
            value={formData.category_id}
            onChange={handleChange}
            fullWidth
            margin="dense"
            required
          >
            {categories.length > 0 ? (
              categories.map((cat) => (
                <MenuItem key={cat.id || cat._id} value={cat.id || cat._id}>
                  {cat.name || cat.title}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No categories available</MenuItem>
            )}
          </TextField>

          <TextField
            label="Working Days"
            name="working_days"
            value={formData.working_days}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Working Hours"
            name="working_hours"
            value={formData.working_hours}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Pricing Range (e.g. KSh 500 - 1000)"
            name="pricing_range"
            value={formData.pricing_range}
            onChange={handleChange}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Extra Info"
            name="extra_info"
            value={formData.extra_info}
            onChange={handleChange}
            fullWidth
            margin="dense"
            multiline
            rows={3}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.verified}
                onChange={handleChange}
                name="verified"
              />
            }
            label="Verified"
          />
          <Button variant="contained" component="label" sx={{ mt: 2 }}>
            Upload Image
            <input type="file" hidden onChange={handleChange} name="image" />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary">
            {isEditing ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ServiceProviderForm;
